import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter, DialogClose, Input } from '@vnetwork/ui/primitives';
import { readProductConfigOptions } from '../product-config-option';
import { useServicePriceTotal } from '../use-service-price-total';
import { TemplatePanel } from '../template-panel';
import { StatementToggle } from '../statement-toggle';
import { isStatementable } from '../statementable-templates';
import type { ProductTemplateProps } from '../template-props';
import type { ContractFormValues } from '../../contract-form';
import type { ContractServiceConfigOption } from '@/api/contracts';
import { CdnRegionConfig } from './cdn-region-config';

const UNLIMITED = 999999999;

// Region config types used by CDN — both `bandwidth` (main) and `base-config` (addon).
const REGION_TYPES = ['bandwidth', 'base-config'] as const;

function isRegionConfig(opt: ContractServiceConfigOption): boolean {
  return REGION_TYPES.includes(opt.type as (typeof REGION_TYPES)[number]);
}

/**
 * Build the initial `service_configoptions` from the product's config options.
 * Each product config option becomes one region entry. Mirrors the legacy
 * CDN `Configuration/index.tsx` seed logic: prices come from product pricing
 * steps if present, else a single open-ended range at 0.
 */
function buildInitialOptions(detail: unknown): ContractServiceConfigOption[] {
  const productOpts = readProductConfigOptions(detail);
  if (productOpts.length === 0) {
    // Fallback: a single global bandwidth region when the product has no options.
    return [
      {
        name: 'global',
        type: 'bandwidth',
        unit: 'GB',
        prices: [{ price_start: 0, price_end: UNLIMITED, price_per_unit: 0 }],
        price_config: { method: 'progressive' },
        commitment_prepaid: true,
        statementable: true,
        commitment_value: 0,
        commitment_price_per_unit: 0,
        commitment_time: 1,
      },
    ];
  }
  return productOpts.map((opt) => ({
    name: opt.name,
    type: opt.type ?? 'bandwidth',
    unit: opt.unit ?? 'GB',
    prices:
      opt.product_pricing_steps && opt.product_pricing_steps.length > 0
        ? opt.product_pricing_steps.map(({ price_start, price_end, price_per_unit }) => ({
            price_start,
            price_end,
            price_per_unit,
          }))
        : [{ price_start: 0, price_end: UNLIMITED, price_per_unit: 0 }],
    price_config: { method: 'progressive' },
    commitment_prepaid: true,
    statementable: true,
    commitment_value: 0,
    commitment_price_per_unit: 0,
    commitment_time: 1,
  }));
}

/**
 * SwiftFederation.CDN template — CDN bandwidth commitment + multi-region tiered
 * pricing. Writes all config into `products.{index}.service_configoptions`.
 *
 * Supports:
 * - Seeding initial region configs from `detail.product_config_options`
 * - Adding a new region (with a text prompt for the region name)
 * - Merging all regions into one global config (sums commitment values)
 */
export function CdnTemplate({ index, templateName, detail }: ProductTemplateProps) {
  const { control, setValue, getValues } = useFormContext<ContractFormValues>();
  const optsPath = `products.${index}.service_configoptions` as const;
  const [addRegionOpen, setAddRegionOpen] = useState(false);
  const [newRegionName, setNewRegionName] = useState('');
  const options = useWatch({ control, name: optsPath }) as
    | ContractServiceConfigOption[]
    | undefined;

  // Derive register price from Σ(commitment × unit price) so a prepaid bandwidth
  // commitment shows in the contract Total (matches legacy CDN, which runs the
  // same Bundle-model price total). Usage overage stays for monthly statement.
  useServicePriceTotal(index);

  // Seed once on mount — guard prevents overwriting user edits on re-render.
  useEffect(() => {
    const current = getValues(optsPath);
    if (Array.isArray(current) && current.length > 0) return;
    setValue(optsPath, buildInitialOptions(detail) as never, { shouldDirty: true });
  }, [optsPath, getValues, setValue, detail]);

  const regionConfigs = (options ?? []).filter(isRegionConfig);
  const otherConfigs = (options ?? []).filter((o) => !isRegionConfig(o));
  const regionCount = regionConfigs.length;

  // Merge all region configs into one global entry, summing commitment values.
  // Preserves the type of the first region so addon (base-config) stays correct.
  const handleMerge = () => {
    if (regionCount <= 1) return;
    const merged: ContractServiceConfigOption = {
      name: 'global',
      type: regionConfigs[0]?.type ?? 'bandwidth',
      unit: regionConfigs[0]?.unit ?? 'GB',
      prices: regionConfigs[0]?.prices ?? [
        { price_start: 0, price_end: UNLIMITED, price_per_unit: 0 },
      ],
      price_config: regionConfigs[0]?.price_config ?? { method: 'progressive' },
      commitment_prepaid: regionConfigs[0]?.commitment_prepaid ?? true,
      statementable: regionConfigs[0]?.statementable ?? true,
      commitment_value: regionConfigs.reduce(
        (sum, r) => sum + Number(r.commitment_value ?? 0),
        0,
      ),
      commitment_price_per_unit: regionConfigs[0]?.commitment_price_per_unit,
      commitment_time: regionConfigs[0]?.commitment_time,
    };
    setValue(optsPath, [...otherConfigs, merged] as never, { shouldDirty: true });
  };

  // Confirm a region name (from dialog) and append a new bandwidth entry.
  const commitAddRegion = () => {
    const regionName = newRegionName.trim();
    if (!regionName) return;
    const existingType = regionConfigs[0]?.type ?? 'bandwidth';
    const newRegion: ContractServiceConfigOption = {
      name: regionName.toLowerCase(),
      type: existingType,
      unit: regionConfigs[0]?.unit ?? 'GB',
      prices: [{ price_start: 0, price_end: UNLIMITED, price_per_unit: 0 }],
      price_config: { method: 'progressive' },
      commitment_prepaid: true,
      statementable: true,
      commitment_value: 0,
      commitment_price_per_unit: 0,
      commitment_time: 1,
    };
    setValue(optsPath, [...(options ?? []), newRegion] as never, { shouldDirty: true });
    setNewRegionName('');
    setAddRegionOpen(false);
  };

  const handleRemoveRegion = (regionIndex: number) => {
    // regionIndex is relative to regionConfigs — map back to options array index.
    const target = regionConfigs[regionIndex];
    const next = (options ?? []).filter((o) => o !== target);
    setValue(optsPath, next as never, { shouldDirty: true });
  };

  if (!Array.isArray(options) || options.length === 0) return null;

  const showStatement = isStatementable(templateName);

  return (
    <TemplatePanel title="CDN configuration">
      {/* Region action bar — mirrors legacy CDN header buttons. */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 'var(--vn-space-2)',
        }}
      >
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={regionCount <= 1}
          onClick={handleMerge}
        >
          Merge bandwidth
        </Button>
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={() => { setNewRegionName(''); setAddRegionOpen(true); }}
        >
          Add region
        </Button>
      </div>

      {/* Region name dialog — replaces window.prompt for accessible input. */}
      <Dialog open={addRegionOpen} onOpenChange={setAddRegionOpen}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Add region</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Input
              autoFocus
              placeholder="Region name (e.g. HN, HCM, SG)"
              value={newRegionName}
              onChange={(e) => setNewRegionName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitAddRegion(); } }}
            />
          </DialogBody>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="secondary" size="sm">Cancel</Button>
            </DialogClose>
            <Button type="button" variant="primary" size="sm" disabled={!newRegionName.trim()} onClick={commitAddRegion}>
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* One card per region config option, with optional statement toggle. */}
      {regionConfigs.map((region, i) => {
        const regionOptPath = `${optsPath}.${(options ?? []).indexOf(region)}`;
        return (
          <div key={`${region.name}-${i}`} style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            <CdnRegionConfig
              optPath={regionOptPath}
              regionName={region.name ?? `region-${i}`}
              totalRegions={regionCount}
              onRemove={() => handleRemoveRegion(i)}
            />
            {showStatement && (
              <StatementToggle optPath={regionOptPath} />
            )}
          </div>
        );
      })}
    </TemplatePanel>
  );
}
